import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import ReactQuill from "react-quill";
import { marked } from "marked";
import {
  BoardLabel,
  BoardDueDate,
  BoardAvatar,
  BoardAvatarStack,
  BoardButton,
  BoardIconButton,
} from "../../atoms/BoardDesignSystem";
import { BOARD_Z_INDEX, formatDueDate, formatEstimatedHours } from "../../../app/utils/projectBoard/boardDesignTokens";
import BoardLabelPicker from "../BoardLabelPicker/BoardLabelPicker";
import BoardMemberPicker from "../BoardMemberPicker/BoardMemberPicker";
import BoardChecklistEditor from "../BoardChecklistEditor/BoardChecklistEditor";
import BoardAttachmentPanel from "../BoardAttachmentPanel/BoardAttachmentPanel";
import BoardAttachmentViewer from "../BoardAttachmentViewer/BoardAttachmentViewer";
import {
  attachmentFromActivityMeta,
  parseActivityMetadata,
} from "../../../app/utils/projectBoard/projectBoardAttachment";
import BoardDropdownPortal from "../BoardDropdownPortal/BoardDropdownPortal";
import ConfirmModal from "../../atoms/ConfirmModal/ConfirmModal";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import "react-datepicker/dist/react-datepicker.css";
import "react-quill/dist/quill.snow.css";
import "./BoardCardModal.scss";

marked.setOptions({ breaks: true, gfm: true });

const isQuillEmpty = (html) => {
  const value = String(html ?? "").trim();
  if (!value) return true;
  if (value === "<p><br></p>" || value === "<p></p>") return true;
  return !value.replace(/<[^>]+>/g, "").trim();
};

const looksLikeHtml = (value) => /<[a-z][\s\S]*>/i.test(String(value ?? "").trim());

const DESCRIPTION_QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const COMMENT_QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const toCommentHtml = (body) => {
  if (isQuillEmpty(body)) return "";
  return looksLikeHtml(body) ? body : marked.parse(body);
};

const PANEL = {
  NONE: "",
  LABELS: "labels",
  MEMBERS: "members",
  DUE: "due",
  CHECKLIST: "checklist",
  ATTACHMENT: "attachment",
};

const MODAL_POPOVER_Z = BOARD_Z_INDEX.modal + 10;

const ToolbarBtn = ({ icon, label, active, onClick, btnRef }) => (
  <button
    ref={btnRef}
    type="button"
    className={`pb-card-modal__toolbar-btn ${active ? "is-active" : ""}`}
    onClick={onClick}
  >
    <span className="pb-card-modal__toolbar-icon" aria-hidden="true">
      {icon}
    </span>
    {label}
  </button>
);

const buildCardFeed = (comments, activity, showDetails) => {
  const commentItems = (comments || []).map((comment) => ({
    kind: "comment",
    id: comment.commentId,
    createdAt: comment.createdAt,
    comment,
  }));

  const activityItems = showDetails
    ? (activity || [])
      .filter((item) => item.actionType !== "commented")
      .map((item) => ({
        kind: "activity",
        id: item.activityId,
        createdAt: item.createdAt,
        activity: item,
      }))
    : [];

  return [...commentItems, ...activityItems].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
};

const BoardCardModal = ({
  card,
  boardId,
  listName,
  comments = [],
  cardActivity = [],
  boardMembers = [],
  canManage = false,
  canEdit = false,
  onClose,
  onUpdate,
  onDelete,
  onCommentAdd,
  onAttachmentChange,
}) => {
  const [activePanel, setActivePanel] = useState(PANEL.NONE);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [labelIds, setLabelIds] = useState([]);
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [dueDate, setDueDate] = useState(null);
  const [estimatedHours, setEstimatedHours] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [editingDesc, setEditingDesc] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const membersBtnRef = useRef(null);
  const labelsBtnRef = useRef(null);
  const dueBtnRef = useRef(null);
  const attachmentBtnRef = useRef(null);
  const membersPanelRef = useRef(null);
  const labelsPanelRef = useRef(null);
  const duePanelRef = useRef(null);
  const attachmentPanelRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (activePanel !== PANEL.NONE) {
        setActivePanel(PANEL.NONE);
        return;
      }
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, activePanel]);

  useEffect(() => {
    if (!card) return;
    setTitle(card.title || "");
    setDescription(card.description || "");
    setLabelIds(card.labelIds || []);
    setAssigneeIds(card.assigneeIds || []);
    setDueDate(card.dueDate ? new Date(card.dueDate) : null);
    setEstimatedHours(
      card.estimatedHours != null && card.estimatedHours !== ""
        ? String(card.estimatedHours)
        : ""
    );
    setChecklist(card.checklist || []);
    setActivePanel(PANEL.NONE);
    setEditingDesc(false);
    setCommentText("");
    setShowDetails(false);
    setPreviewAttachment(null);
    setDeleteConfirmOpen(false);
    setDeleting(false);
  }, [card]);

  useEffect(() => {
    const isOutside = (anchorRef, panelRef, target) =>
      !anchorRef.current?.contains(target) && !panelRef.current?.contains(target);

    const onDocClick = (e) => {
      if (activePanel === PANEL.MEMBERS && isOutside(membersBtnRef, membersPanelRef, e.target)) {
        setActivePanel(PANEL.NONE);
      }
      if (activePanel === PANEL.LABELS && isOutside(labelsBtnRef, labelsPanelRef, e.target)) {
        setActivePanel(PANEL.NONE);
      }
      if (activePanel === PANEL.DUE && isOutside(dueBtnRef, duePanelRef, e.target)) {
        setActivePanel(PANEL.NONE);
      }
      if (activePanel === PANEL.ATTACHMENT && isOutside(attachmentBtnRef, attachmentPanelRef, e.target)) {
        setActivePanel(PANEL.NONE);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [activePanel]);

  const descriptionHtml = useMemo(() => {
    if (isQuillEmpty(description)) return "";
    return looksLikeHtml(description) ? description : marked.parse(description);
  }, [description]);

  const hasDescription = !isQuillEmpty(description);

  const assigneeMembers = useMemo(() => {
    const ids = assigneeIds.map(Number);
    return boardMembers.filter((m) => ids.includes(Number(m.id || m.userId)));
  }, [assigneeIds, boardMembers]);

  const memberMap = useMemo(
    () => new Map(boardMembers.map((m) => [Number(m.id || m.userId), m])),
    [boardMembers]
  );

  const cardActivityDetails = useMemo(
    () => (cardActivity || []).filter((item) => item.actionType !== "commented"),
    [cardActivity]
  );

  const feedItems = useMemo(
    () => buildCardFeed(comments, cardActivity, showDetails),
    [comments, cardActivity, showDetails]
  );

  if (!card) return null;

  const openAttachmentPreview = (attachment) => {
    if (!attachment?.attachmentId || !card?.cardId) return;
    setPreviewAttachment({ ...attachment, cardId: card.cardId });
  };
  const canComment = canEdit;
  const canAttach = canEdit;
  const showChecklist = checklist.length > 0 || activePanel === PANEL.CHECKLIST;
  const hasMeta = labelIds.length > 0 || assigneeMembers.length > 0 || dueDate
    || estimatedHours !== "" || canEdit
    || (card.actualHours != null && card.actualHours !== "")
    || Boolean(card.actualHoursOverReason);

  const togglePanel = (panel) => {
    setActivePanel((prev) => (prev === panel ? PANEL.NONE : panel));
  };

  const saveCard = async (patch, successMsg = "Card updated.", { requireManage = false } = {}) => {
    if (saving) return;
    if (requireManage && !canManage) return;
    if (!requireManage && !canEdit) return;
    setSaving(true);
    try {
      await onUpdate?.(card.cardId, patch);
      showSuccessToast(successMsg);
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message || error?.message || "Failed to update card.";
      showErrorToast(msg);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTitle = async () => {
    const trimmed = title.trim();
    if (!trimmed || trimmed === card.title) return;
    await saveCard({ title: trimmed }, "Card updated.", { requireManage: true });
  };

  const handleSaveDescription = async () => {
    const normalized = isQuillEmpty(description) ? "" : description;
    setDescription(normalized);
    setEditingDesc(false);
    if (normalized === (card.description || "")) return;
    await saveCard({ description: normalized });
  };

  const handleLabelsChange = async (next) => {
    setLabelIds(next);
    await saveCard({ labelIds: next });
  };

  const handleAssigneesChange = async (next) => {
    setAssigneeIds(next);
    await saveCard({ assigneeIds: next }, "Card updated.", { requireManage: true });
  };

  const handleDueChange = async (date) => {
    setDueDate(date);
    await saveCard(
      { dueDate: date ? date.toISOString() : null },
      "Card updated.",
      { requireManage: true }
    );
  };

  const handleEstimatedHoursSave = async () => {
    const trimmed = estimatedHours.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    if (trimmed !== "" && (Number.isNaN(parsed) || parsed < 0)) {
      setEstimatedHours(
        card.estimatedHours != null && card.estimatedHours !== ""
          ? String(card.estimatedHours)
          : ""
      );
      showErrorToast("Estimated hours must be a non-negative number.");
      return;
    }
    const current = card.estimatedHours != null && card.estimatedHours !== ""
      ? Number(card.estimatedHours)
      : null;
    if (parsed === current) return;
    await saveCard({ estimatedHours: parsed });
  };

  const handleChecklistChange = async (next) => {
    setChecklist(next);
    await saveCard({ checklist: next });
  };

  const handlePostComment = async () => {
    if (isQuillEmpty(commentText) || postingComment) return;
    setPostingComment(true);
    try {
      await onCommentAdd?.(card.cardId, commentText);
      setCommentText("");
      showSuccessToast("Comment added.");
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message || error?.message || "Failed to add comment.";
      showErrorToast(msg);
    } finally {
      setPostingComment(false);
    }
  };

  const handleDeleteCard = async () => {
    if (!canManage || !onDelete || deleting) return;
    setDeleting(true);
    try {
      await onDelete(card.cardId);
      setDeleteConfirmOpen(false);
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message || error?.message || "Failed to delete card.";
      showErrorToast(msg);
    } finally {
      setDeleting(false);
    }
  };

  const formatFeedTime = (dateStr) =>
    new Date(dateStr).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });

  const resolveActivityActor = (item) => {
    if (item.actor?.name) return item.actor;
    const member = memberMap.get(Number(item.actorId));
    if (member) return member;
    return {
      id: item.actorId,
      name: "Unknown",
      imageUrl: null,
    };
  };

  return createPortal(
    <div
      className="pb-modal__overlay"
      onClick={() => {
        if (activePanel !== PANEL.NONE) {
          setActivePanel(PANEL.NONE);
          return;
        }
        onClose?.();
      }}
      role="presentation"
    >
      <div
        className="pb-modal__dialog pb-card-modal pb-card-modal--trello"
        role="dialog"
        aria-modal="true"
        aria-labelledby="board-card-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pb-card-modal__topbar">
          <span className="pb-card-modal__list-badge">
            in list <strong>{listName}</strong>
          </span>
          <div className="pb-card-modal__topbar-actions">
            {canManage && onDelete && (
              <BoardButton
                variant="ghost"
                size="sm"
                className="pb-card-modal__delete-btn"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={saving || deleting}
              >
                Delete
              </BoardButton>
            )}
            <BoardIconButton icon="✕" label="Close" onClick={onClose} />
          </div>
        </div>

        <div className="pb-card-modal__layout">
          <main className="pb-card-modal__main">
            {card.coverUrl && (
              <div
                className="pb-card-modal__cover"
                style={{ backgroundImage: `url(${card.coverUrl})` }}
              />
            )}

            <div className="pb-card-modal__title-block">
              {card.ticketNo && (
                <span className="pb-card-modal__ticket">{card.ticketNo}</span>
              )}
              {canManage ? (
                <textarea
                  id="board-card-modal-title"
                  className="pb-card-modal__title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={handleSaveTitle}
                  rows={1}
                  placeholder="Card title"
                />
              ) : (
                <h2 id="board-card-modal-title" className="pb-card-modal__title">
                  {card.title}
                </h2>
              )}
            </div>

            {(canEdit || canManage) && (
              <div className="pb-card-modal__toolbar">
                {canManage && (
                  <>
                    <ToolbarBtn
                      icon="👤"
                      label="Assignee"
                      active={activePanel === PANEL.MEMBERS}
                      btnRef={membersBtnRef}
                      onClick={() => togglePanel(PANEL.MEMBERS)}
                    />
                    <ToolbarBtn
                      icon="🕐"
                      label="Dates"
                      active={activePanel === PANEL.DUE}
                      btnRef={dueBtnRef}
                      onClick={() => togglePanel(PANEL.DUE)}
                    />
                  </>
                )}
                {canEdit && (
                  <>
                    <ToolbarBtn
                      icon="🏷"
                      label="Labels"
                      active={activePanel === PANEL.LABELS}
                      btnRef={labelsBtnRef}
                      onClick={() => togglePanel(PANEL.LABELS)}
                    />
                    <ToolbarBtn
                      icon="☑"
                      label="Checklist"
                      active={activePanel === PANEL.CHECKLIST}
                      onClick={() => togglePanel(PANEL.CHECKLIST)}
                    />
                    {/* <ToolbarBtn
                      icon="📎"
                      label="Attachment"
                      active={activePanel === PANEL.ATTACHMENT}
                      btnRef={attachmentBtnRef}
                      onClick={() => togglePanel(PANEL.ATTACHMENT)}
                    /> */}
                  </>
                )}
              </div>
            )}

            {hasMeta && (
              <div className="pb-card-modal__meta">
                {labelIds.length > 0 && (
                  <div className="pb-card-modal__meta-row">
                    <span className="pb-card-modal__meta-label">Labels</span>
                    <div className="pb-card-modal__labels">
                      {labelIds.map((id) => (
                        <BoardLabel key={id} labelId={id} boardId={boardId} named />
                      ))}
                    </div>
                  </div>
                )}
                {assigneeMembers.length > 0 && (
                  <div className="pb-card-modal__meta-row">
                    <span className="pb-card-modal__meta-label">Members</span>
                    <BoardAvatarStack members={assigneeMembers} max={8} size="md" />
                  </div>
                )}
                {dueDate && (
                  <div className="pb-card-modal__meta-row">
                    <span className="pb-card-modal__meta-label">Due date</span>
                    <BoardDueDate dueDate={dueDate.toISOString()} />
                  </div>
                )}
                {card.actualHours != null && card.actualHours !== "" && (
                  <div className="pb-card-modal__meta-row">
                    <span className="pb-card-modal__meta-label">Actual hours</span>
                    <span className="pb-card-modal__hours-readonly">
                      {formatEstimatedHours(card.actualHours)}
                    </span>
                  </div>
                )}
                {card.actualHoursOverReason && (
                  <div className="pb-card-modal__meta-row pb-card-modal__meta-row--stacked">
                    <span className="pb-card-modal__meta-label">Over estimate</span>
                    <p className="pb-card-modal__over-reason">{card.actualHoursOverReason}</p>
                  </div>
                )}
                {(estimatedHours !== "" || canEdit) && (
                  <div className="pb-card-modal__meta-row">
                    <span className="pb-card-modal__meta-label">Estimated hours</span>
                    {canEdit ? (
                      <div className="pb-card-modal__hours-field">
                        <input
                          type="number"
                          className="pb-card-modal__hours-input"
                          min="0"
                          step="0.25"
                          value={estimatedHours}
                          onChange={(e) => setEstimatedHours(e.target.value)}
                          onBlur={handleEstimatedHoursSave}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                          }}
                          placeholder="e.g. 4"
                          disabled={saving}
                        />
                        <span className="pb-card-modal__hours-suffix">hrs</span>
                      </div>
                    ) : estimatedHours ? (
                      <span className="pb-card-modal__hours-readonly">
                        {formatEstimatedHours(estimatedHours)}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            <section className="pb-card-modal__section">
              <div className="pb-card-modal__section-head">
                <span className="pb-card-modal__section-icon" aria-hidden="true">
                  ☰
                </span>
                <h3 className="pb-card-modal__section-title">Description</h3>
                {canEdit && hasDescription && !editingDesc && (
                  <button
                    type="button"
                    className="pb-card-modal__edit-link"
                    onClick={() => setEditingDesc(true)}
                  >
                    Edit
                  </button>
                )}
              </div>
              {canEdit && editingDesc ? (
                <div className="pb-card-modal__desc-edit">
                  <ReactQuill
                    theme="snow"
                    className="pb-card-modal__desc-quill"
                    value={description}
                    onChange={setDescription}
                    modules={DESCRIPTION_QUILL_MODULES}
                    placeholder="Add a more detailed description..."
                  />
                  <div className="pb-card-modal__desc-actions">
                    <BoardButton variant="primary" size="sm" onClick={handleSaveDescription}>
                      Save
                    </BoardButton>
                    <BoardButton
                      variant="ghost-dark"
                      size="sm"
                      onClick={() => {
                        setDescription(card.description || "");
                        setEditingDesc(false);
                      }}
                    >
                      Cancel
                    </BoardButton>
                  </div>
                </div>
              ) : hasDescription ? (
                <div
                  className="ql-snow pb-card-modal__quill-content pb-card-modal__description pb-card-modal__description--md"
                  onClick={() => canEdit && setEditingDesc(true)}
                  role={canEdit ? "button" : undefined}
                  tabIndex={canEdit ? 0 : undefined}
                >
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  />
                </div>
              ) : canEdit ? (
                <button
                  type="button"
                  className="pb-card-modal__desc-placeholder"
                  onClick={() => setEditingDesc(true)}
                >
                  Add a more detailed description...
                </button>
              ) : null}
            </section>

            {showChecklist && (
              <section className="pb-card-modal__section">
                <div className="pb-card-modal__section-head">
                  <span className="pb-card-modal__section-icon" aria-hidden="true">
                    ☑
                  </span>
                  <h3 className="pb-card-modal__section-title">Checklist</h3>
                </div>
                <BoardChecklistEditor
                  items={checklist}
                  onChange={handleChecklistChange}
                  disabled={!canEdit || saving}
                />
              </section>
            )}

            {canAttach && (
              <section className="pb-card-modal__section">
                <div className="pb-card-modal__section-head">
                  <span className="pb-card-modal__section-icon" aria-hidden="true">
                    📎
                  </span>
                  <h3 className="pb-card-modal__section-title">Attachments</h3>
                </div>
                <BoardAttachmentPanel
                  cardId={card.cardId}
                  attachmentCount={card.attachmentCount || 0}
                  canUpload={canAttach}
                  disabled={false}
                  onUpload={onAttachmentChange}
                  previewAttachment={previewAttachment}
                  onPreviewAttachment={setPreviewAttachment}
                />
              </section>
            )}
          </main>

          <aside className="pb-card-modal__activity">
            <div className="pb-card-modal__activity-head">
              <span className="pb-card-modal__section-icon" aria-hidden="true">
                💬
              </span>
              <h3 className="pb-card-modal__section-title">Comments and activity</h3>
            </div>

            {canComment && (
              <div className="pb-card-modal__comment-compose">
                <ReactQuill
                  theme="snow"
                  className="pb-card-modal__comment-quill"
                  value={commentText}
                  onChange={setCommentText}
                  modules={COMMENT_QUILL_MODULES}
                  placeholder="Write a comment..."
                />
                {!isQuillEmpty(commentText) && (
                  <BoardButton
                    variant="primary"
                    size="sm"
                    onClick={handlePostComment}
                    disabled={postingComment}
                  >
                    {postingComment ? "Saving..." : "Save"}
                  </BoardButton>
                )}
              </div>
            )}

            {cardActivityDetails.length > 0 && (
              <button
                type="button"
                className="pb-card-modal__details-toggle"
                onClick={() => setShowDetails((open) => !open)}
              >
                {showDetails ? "Hide details" : "Show details"}
              </button>
            )}

            <div className="pb-card-modal__activity-feed">
              {feedItems.map((item) => {
                if (item.kind === "comment") {
                  const c = item.comment;
                  return (
                    <div key={`comment-${c.commentId}`} className="pb-card-modal__activity-item">
                      <BoardAvatar
                        name={c.author?.name}
                        userId={c.author?.id}
                        imageUrl={c.author?.avatarUrl || c.author?.imageUrl}
                        size="md"
                      />
                      <div className="pb-card-modal__activity-body">
                        <p className="pb-card-modal__activity-text">
                          <strong>{c.author?.name}</strong>
                        </p>
                        <div className="ql-snow pb-card-modal__quill-content">
                          <div
                            className="ql-editor pb-card-modal__activity-html"
                            dangerouslySetInnerHTML={{ __html: toCommentHtml(c.body) }}
                          />
                        </div>
                        <time className="pb-card-modal__activity-time" dateTime={c.createdAt}>
                          {formatFeedTime(c.createdAt)}
                        </time>
                      </div>
                    </div>
                  );
                }

                const activityItem = item.activity;
                const actor = resolveActivityActor(activityItem);
                const activityMeta = parseActivityMetadata(activityItem.metadata);
                const isAttachmentActivity = activityItem.actionType === "attachment";
                const activityAttachment = attachmentFromActivityMeta(card.cardId, activityMeta);
                return (
                  <div
                    key={`activity-${activityItem.activityId}`}
                    className="pb-card-modal__activity-item pb-card-modal__activity-item--detail"
                  >
                    <BoardAvatar
                      name={actor.name}
                      userId={actor.id || actor.userId}
                      imageUrl={actor.imageUrl || actor.avatarUrl}
                      size="md"
                    />
                    <div className="pb-card-modal__activity-body">
                      <p className="pb-card-modal__activity-text">
                        <strong>{actor.name}</strong>
                        {" "}
                        {isAttachmentActivity && activityAttachment ? (
                          <>
                            attached{" "}
                            <button
                              type="button"
                              className="pb-card-modal__activity-link"
                              onClick={() => openAttachmentPreview(activityAttachment)}
                            >
                              {activityAttachment.fileName || activityItem.description?.replace(/^attached\s+/i, "")}
                            </button>
                          </>
                        ) : (
                          activityItem.description
                        )}
                      </p>
                      <time
                        className="pb-card-modal__activity-time pb-card-modal__activity-time--link"
                        dateTime={activityItem.createdAt}
                      >
                        {formatFeedTime(activityItem.createdAt)}
                      </time>
                    </div>
                  </div>
                );
              })}
              {feedItems.length === 0 && (
                <p className="pb-card-modal__activity-empty">No comments yet.</p>
              )}
            </div>
          </aside>
        </div>
      </div>

      <BoardDropdownPortal
        open={activePanel === PANEL.MEMBERS}
        anchorRef={membersBtnRef}
        menuRef={membersPanelRef}
        placement="bottom-start"
        zIndex={MODAL_POPOVER_Z}
        className="pb-card-modal__action-popover"
      >
        <BoardMemberPicker
          members={boardMembers}
          selectedIds={assigneeIds}
          onChange={handleAssigneesChange}
          onClose={() => setActivePanel(PANEL.NONE)}
          disabled={saving}
        />
      </BoardDropdownPortal>

      <BoardDropdownPortal
        open={activePanel === PANEL.LABELS}
        anchorRef={labelsBtnRef}
        menuRef={labelsPanelRef}
        placement="bottom-start"
        zIndex={MODAL_POPOVER_Z}
        className="pb-card-modal__action-popover"
      >
        <BoardLabelPicker
          boardId={boardId}
          selectedIds={labelIds}
          onChange={handleLabelsChange}
          disabled={saving}
          onClose={() => setActivePanel(PANEL.NONE)}
        />
      </BoardDropdownPortal>

      <BoardDropdownPortal
        open={activePanel === PANEL.DUE}
        anchorRef={dueBtnRef}
        menuRef={duePanelRef}
        placement="bottom-start"
        zIndex={MODAL_POPOVER_Z}
        className="pb-card-modal__action-popover pb-card-modal__due-popover"
      >
        <div
          className="pb-due-date-picker"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="pb-due-date-picker__header">
            <h4 className="pb-due-date-picker__title">Dates</h4>
            <button
              type="button"
              className="pb-due-date-picker__close"
              onClick={() => setActivePanel(PANEL.NONE)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {dueDate && (
            <div className="pb-due-date-picker__summary">
              <span className="pb-due-date-picker__summary-label">Due date</span>
              <span className="pb-due-date-picker__summary-value">
                {formatDueDate(dueDate.toISOString())}
                {" · "}
                {dueDate.toLocaleTimeString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </div>
          )}

          <div className="pb-due-date-picker__calendar">
            <DatePicker
              selected={dueDate}
              onChange={handleDueChange}
              showTimeSelect
              timeIntervals={15}
              inline
              calendarClassName="pb-due-date-picker__datepicker"
              dateFormat="MMM d, yyyy h:mm aa"
              disabled={!canManage}
            />
          </div>

          {canManage && dueDate && (
            <button
              type="button"
              className="pb-due-date-picker__remove"
              onClick={() => handleDueChange(null)}
              disabled={saving}
            >
              Remove due date
            </button>
          )}
        </div>
      </BoardDropdownPortal>

      <BoardDropdownPortal
        open={activePanel === PANEL.ATTACHMENT}
        anchorRef={attachmentBtnRef}
        menuRef={attachmentPanelRef}
        placement="bottom-start"
        zIndex={MODAL_POPOVER_Z}
        className="pb-card-modal__action-popover"
      >
        <BoardAttachmentPanel
          cardId={card.cardId}
          attachmentCount={card.attachmentCount || 0}
          canUpload={canAttach}
          compact
          disabled={false}
          onUpload={onAttachmentChange}
          previewAttachment={previewAttachment}
          onPreviewAttachment={setPreviewAttachment}
        />
      </BoardDropdownPortal>

      {previewAttachment && (
        <BoardAttachmentViewer
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteCard}
        title="Delete card?"
        content={`Delete "${card.title || "this card"}"? This cannot be undone.`}
        confirmText="Delete card"
        confirmLoading={deleting}
        zIndex={BOARD_Z_INDEX.modal + 20}
      />
    </div>,
    document.body
  );
};

export default BoardCardModal;
